/**
 * Inline SVG icons.
 *
 * No emoji anywhere — they render inconsistently across platforms and several
 * are simply missing on Windows. No icon font either, since that would be a
 * third-party asset to host and a flash of missing glyphs to manage.
 *
 * Every icon draws with `currentColor` so it inherits the palette automatically
 * and needs no per-appearance handling. Stroke widths are deliberately light to
 * match the hairline treatment; these are labels, not decoration.
 */

interface IconProps {
  readonly className?: string;
}

const base = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
};

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 6.5 8 10.5 12 6.5" />
    </svg>
  );
}

export function CalculatorIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <rect x="3" y="1.75" width="10" height="12.5" rx="1.5" />
      <path d="M5.5 4.75h5" />
      <path d="M5.75 8h.01M8 8h.01M10.25 8h.01M5.75 10.75h.01M8 10.75h.01M10.25 10.75h.01" />
    </svg>
  );
}

export function DotsVerticalIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M8 3.5h.01M8 8h.01M8 12.5h.01" />
    </svg>
  );
}

export function PlusIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M8 3.5v9M3.5 8h9" />
    </svg>
  );
}

export function InfoIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="8" cy="8" r="6.25" />
      <path d="M8 7.25v4" />
      <path d="M8 5h.01" />
    </svg>
  );
}

/*
 * "Make this the base" — a star, drawn open rather than filled so it reads as
 * an action to take, not a state already true. Five points at 16px is muddy at
 * this stroke weight, so it is simplified to a four-point sparkle, which stays
 * legible and is unambiguous next to its label.
 */
export function StarIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M8 2.25 9.6 6.4 13.75 8 9.6 9.6 8 13.75 6.4 9.6 2.25 8 6.4 6.4Z" />
    </svg>
  );
}

export function CheckIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="7.25" cy="7.25" r="4.25" />
      <path d="m10.5 10.5 3 3" />
    </svg>
  );
}

export function TrashIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M3 4.5h10" />
      <path d="M6.25 4.5V3.25h3.5V4.5" />
      <path d="M4.25 4.5 5 13.25h6l.75-8.75" />
    </svg>
  );
}
